// src/components/auth/MinhasNotificacoesPage.js
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { Bell, BellOff, Trash2, Check, Loader2, XCircle, Settings } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import Pagination from '../common/Pagination';
import {
    useNotificacoes,
    usePreferenciasNotificacoes,
    useMutateNotificacoes,
} from '../../hooks/useNotificacoes';

const TABS = { lista: 'lista', preferencias: 'preferencias' };

// ✅ CORREÇÃO: esses rótulos eram uma taxonomia inventada no frontend que
// nunca bateu com os tipos reais gravados em Notification.tipo (server/
// models/Notification.js) — por isso a tela parecia incompleta (faltava
// 'seguranca', que é real) e o toggle nunca tinha efeito nenhum (o backend
// também nunca lia essa preferência antes de enviar, e o campo nem existia
// declarado no schema do User — era descartado silenciosamente pelo modo
// strict do Mongoose). "Sistema" (in-app) saiu da lista de canais: o
// histórico de notificações sempre é criado, não é um canal opcional.
const CANAL_LABELS = { email: 'Email', sms: 'SMS', push: 'Push' };

const TIPO_LABELS = {
    sistema: 'Sistema',
    pagamento: 'Pagamento',
    agendamento: 'Agendamento',
    atualizacao: 'Atualização',
    alerta: 'Alerta',
    promocao: 'Promoção',
    lembrete: 'Lembrete',
    seguranca: 'Segurança',
};

const MinhasNotificacoesPage = () => {
    const [tab, setTab] = useState(TABS.lista);
    const [currentPage, setCurrentPage] = useState(1);
    const [soNaoLidas, setSoNaoLidas] = useState(false);
    const itemsPerPage = 20;

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: itemsPerPage,
        ...(soNaoLidas && { lida: 'false' }),
    }), [currentPage, soNaoLidas, itemsPerPage]);

    const { data: notifData, isLoading } = useNotificacoes(queryParams);
    const { data: prefs, isLoading: prefsLoading } = usePreferenciasNotificacoes();
    const { marcarLida, marcarTodasLidas, deletarUma, salvarPreferencias } = useMutateNotificacoes();

    const [localPrefs, setLocalPrefs] = useState(null);

    const notificacoes = notifData?.data || [];
    const total = notifData?.count || 0;

    const prefsAtivas = localPrefs || prefs || {};

    const handleMarcarLida = useCallback((id) => {
        marcarLida.mutate(id);
    }, [marcarLida]);

    const handleDeletar = useCallback((id) => {
        deletarUma.mutate(id);
    }, [deletarUma]);

    const handleMarcarTodas = useCallback(() => {
        marcarTodasLidas.mutate();
    }, [marcarTodasLidas]);

    const handlePrefChange = useCallback((tipo, canal, checked) => {
        setLocalPrefs(prev => {
            const base = prev || prefs || {};
            return {
                ...base,
                [tipo]: {
                    ...(base[tipo] || {}),
                    [canal]: checked,
                },
            };
        });
    }, [prefs]);

    const handleSalvarPrefs = useCallback(() => {
        if (!localPrefs) return;
        salvarPreferencias.mutate(localPrefs, {
            onSuccess: () => setLocalPrefs(null),
        });
    }, [localPrefs, salvarPreferencias]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Bell />}
                    title="Minhas Notificações"
                    subtitle={!isLoading && total > 0 ? `${total} notificação${total !== 1 ? 'ões' : ''}` : null}
                    actions={<>
                        {tab === TABS.lista && (
                            <>
                                <button
                                    onClick={() => { setSoNaoLidas(p => !p); setCurrentPage(1); }}
                                    className={`action-button ${soNaoLidas ? 'action-button-primary' : 'action-button-secondary'}`}
                                >
                                    {soNaoLidas ? <Bell size={18} className="icon"/> : <BellOff size={18} className="icon"/>}
                                    {soNaoLidas ? 'Todas' : 'Não lidas'}
                                </button>
                                <button
                                    onClick={handleMarcarTodas}
                                    className="action-button action-button-secondary"
                                    disabled={marcarTodasLidas.isPending}
                                >
                                    <Check size={18} className="icon"/>
                                    Marcar todas lidas
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => setTab(tab === TABS.preferencias ? TABS.lista : TABS.preferencias)}
                            className={`action-button ${tab === TABS.preferencias ? 'action-button-primary' : 'action-button-secondary'}`}
                        >
                            {tab === TABS.preferencias
                                ? <XCircle size={18} className="icon"/>
                                : <Settings size={18} className="icon"/>}
                            {tab === TABS.preferencias ? 'Fechar' : 'Preferências'}
                        </button>
                    </>}
                />

                {/* Painel de Preferências */}
                {tab === TABS.preferencias && (
                    <div className="content-panel">
                        <div className="form-section">
                            <h4 className="form-section-title">Preferências de Notificação</h4>
                        </div>
                        {prefsLoading ? (
                            <div className="table-loading-state" style={{ padding: '2rem' }}>
                                <Loader2 size={24} className="animate-spin"/>
                                <span>Carregando preferências...</span>
                            </div>
                        ) : (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="styled-table">
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left' }}>Tipo</th>
                                                {Object.keys(CANAL_LABELS).map(canal => (
                                                    <th key={canal}>{CANAL_LABELS[canal]}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(TIPO_LABELS).map(([tipo, label]) => (
                                                <tr key={tipo}>
                                                    <td style={{ textAlign: 'left', fontWeight: 500 }}>{label}</td>
                                                    {Object.keys(CANAL_LABELS).map(canal => (
                                                        <td key={canal}>
                                                            <input
                                                                type="checkbox"
                                                                checked={prefsAtivas?.[tipo]?.[canal] ?? true}
                                                                onChange={e => handlePrefChange(tipo, canal, e.target.checked)}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="actions-bar" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
                                    <button
                                        onClick={handleSalvarPrefs}
                                        className="action-button action-button-primary"
                                        disabled={!localPrefs || salvarPreferencias.isPending}
                                    >
                                        {salvarPreferencias.isPending
                                            ? <Loader2 size={18} className="icon animate-spin"/>
                                            : <Check size={18} className="icon"/>}
                                        Salvar Preferências
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {tab === TABS.lista && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin"/>
                                <span>Carregando notificações...</span>
                            </div>
                        ) : notificacoes.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 8 }}></th>
                                        <th style={{ textAlign: 'left' }}>Notificação</th>
                                        <th>Data</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notificacoes.map(n => (
                                        <tr key={n._id} style={{ opacity: n.lida ? 0.6 : 1 }}>
                                            <td style={{ padding: '0 0 0 1rem' }}>
                                                {!n.lida && (
                                                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)' }}/>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'left' }}>
                                                <div style={{ fontWeight: n.lida ? 400 : 600, fontSize: '0.875rem' }}>{n.titulo}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{n.mensagem}</div>
                                            </td>
                                            <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                                {n.createdAt ? new Date(n.createdAt).toLocaleDateString('pt-BR') : '—'}
                                            </td>
                                            <td className="text-right">
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                                    {!n.lida && (
                                                        <button
                                                            onClick={() => handleMarcarLida(n._id)}
                                                            className="action-icon-button"
                                                            data-variant="edit"
                                                            title="Marcar como lida"
                                                            disabled={marcarLida.isPending}
                                                        >
                                                            <Check size={16} className="icon"/>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeletar(n._id)}
                                                        className="action-icon-button"
                                                        data-variant="delete"
                                                        title="Excluir"
                                                        disabled={deletarUma.isPending}
                                                    >
                                                        <Trash2 size={16} className="icon"/>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Bell size={48}/>
                                <h3>Nenhuma notificação</h3>
                                <p>{soNaoLidas ? 'Você não tem notificações não lidas.' : 'Você não tem notificações.'}</p>
                            </div>
                        )}
                    </div>
                    <Pagination
                        totalItems={total}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                </div>
            )}
        </Fragment>
    );
};

export default MinhasNotificacoesPage;
